import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Users, Plus, Search, Download, X, Save, Trash2, Mail, Phone, MapPin, Building2
} from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";

const STATUS = [
  { value: "prospect", label: "Prospect", badge: "badge-warning" },
  { value: "actif", label: "Actif", badge: "badge-success" },
  { value: "inactif", label: "Inactif", badge: "badge-muted" },
];

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => { const { data } = await api.get("/clients"); setItems(data); };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => {
    if (!q) return true;
    const s = `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""} ${c.email || ""} ${c.phone || ""} ${c.city || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const remove = async (id) => {
    if (!(await confirmAction("Supprimer ce client ?"))) return;
    await api.delete(`/clients/${id}`);
    await load();
    toast.success("Client supprimé");
  };

  const exportCsv = () => {
    const tok = document.cookie.split("session_token=")[1]?.split(";")[0] || "";
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/clients/export.csv${tok ? `?token=${tok}` : ""}`;
  };

  return (
    <div data-testid="clients-page" className="max-w-7xl mx-auto">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-[#FFC400] mb-3">
            <Users size={13} /> Module 3 — CRM
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">Clients</h1>
          <p className="text-secondary-text mt-3">{filtered.length} client{filtered.length > 1 ? "s" : ""} · gérez vos prospects, actifs et historique</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-text" />
            <input className="input pl-9 w-64" placeholder="Nom, email, ville..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" />
          </div>
          <button onClick={exportCsv} className="btn btn-outline btn-sm" data-testid="export-clients">
            <Download size={14} /> Exporter CSV
          </button>
          <button onClick={() => setModal("new")} className="btn btn-accent" data-testid="add-client">
            <Plus size={15} /> Nouveau client
          </button>
        </div>
      </header>

      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-tertiary-text">
            <Users size={36} className="mx-auto mb-3 opacity-40" />
            <div className="font-medium text-primary-text mb-1">Aucun client</div>
            <div className="text-sm">Ajoutez votre premier client pour commencer.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nom & prénom</th>
                <th>Société</th>
                <th>Contact</th>
                <th>Adresse</th>
                <th>Statut</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const st = STATUS.find((s) => s.value === c.status) || STATUS[0];
                return (
                  <tr key={c.id} className="cursor-pointer" onClick={() => setModal(c)} data-testid={`client-${c.id}`}>
                    <td>
                      <div className="font-medium">{c.last_name} {c.first_name}</div>
                    </td>
                    <td className="text-secondary-text text-sm">{c.company_name || "—"}</td>
                    <td className="text-sm">
                      {c.email && <div className="text-secondary-text text-xs flex items-center gap-1"><Mail size={11} /> {c.email}</div>}
                      {c.phone && <div className="text-secondary-text text-xs font-mono flex items-center gap-1"><Phone size={11} /> {c.phone}</div>}
                    </td>
                    <td className="text-secondary-text text-sm">{[c.postal_code, c.city].filter(Boolean).join(" ") || "—"}</td>
                    <td><span className={`badge ${st.badge}`}>{st.label}</span></td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => remove(c.id)} className="w-8 h-8 rounded-md border border-[var(--border)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center"
                        data-testid={`client-delete-${c.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && <ClientModal item={modal === "new" ? null : modal} onClose={() => setModal(null)} onSaved={async () => { await load(); setModal(null); }} />}
    </div>
  );
}

function ClientModal({ item, onClose, onSaved }) {
  const blank = {
    first_name: "", last_name: "", company_name: "", email: "", phone: "",
    address: "", postal_code: "", city: "",
    client_type: "particulier",
    siret: "", vat_number: "", routing_id: "", chorus_service_code: "",
    status: "prospect", notes: "",
  };
  const [form, setForm] = useState(item ? { client_type: "particulier", vat_number: "", routing_id: "", chorus_service_code: "", ...item } : blank);
  const [saving, setSaving] = useState(false);

  const isPro = form.client_type === "pro";
  const isPublic = form.client_type === "public";
  const isParticulier = form.client_type === "particulier";

  const save = async () => {
    // Validation front alignée sur le backend
    if (isParticulier && !form.last_name.trim()) { toast.error("Nom de famille requis pour un particulier"); return; }
    if (isPro && !form.company_name.trim()) { toast.error("Raison sociale obligatoire pour un client Pro"); return; }
    if (isPro && form.siret.replace(/\s/g, "").length !== 14) { toast.error("SIRET obligatoire (14 chiffres) pour un client Pro — conformité facturation électronique 2027"); return; }
    if (isPublic && !form.company_name.trim()) { toast.error("Nom de la structure obligatoire"); return; }
    if (isPublic && form.siret.replace(/\s/g, "").length !== 14) { toast.error("SIRET obligatoire (14 chiffres) pour une administration publique"); return; }
    if (isPublic && !form.chorus_service_code.trim()) { toast.error("Code service Chorus Pro obligatoire pour l'administration publique"); return; }

    setSaving(true);
    try {
      const payload = { ...form, siret: (form.siret || "").replace(/\s/g, "") };
      if (item) await api.patch(`/clients/${item.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(item ? "Client mis à jour" : "Client créé");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="client-modal">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="font-semibold text-lg">{item ? "Modifier le client" : "Nouveau client"}</h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* --- Type de client (obligatoire — impacte la conformité Factur-X) --- */}
          <div>
            <label className="label">Type de client * <span className="text-[11px] font-normal" style={{ color: "var(--grey)" }}>(obligatoire — conformité facturation électronique 2027)</span></label>
            <div className="grid grid-cols-3 gap-2" data-testid="client-type-selector">
              {[
                { v: "particulier", l: "Particulier", d: "TNS, personne physique" },
                { v: "pro", l: "Professionnel", d: "Entreprise (SIRET requis)" },
                { v: "public", l: "Administration", d: "Chorus Pro requis" },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setForm({ ...form, client_type: t.v })}
                  className="rounded-lg p-3 text-left transition-colors"
                  style={{
                    border: `2px solid ${form.client_type === t.v ? "#FFC400" : "var(--steel)"}`,
                    background: form.client_type === t.v ? "rgba(255,196,0,0.08)" : "transparent",
                    color: form.client_type === t.v ? "var(--chalk)" : "var(--ink-soft)",
                  }}
                  data-testid={`ct-${t.v}`}
                >
                  <div className="font-semibold text-sm">{t.l}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--grey)" }}>{t.d}</div>
                </button>
              ))}
            </div>
          </div>

          {/* --- Identité --- */}
          {isPro || isPublic ? (
            <div>
              <label className="label">{isPublic ? "Nom de la structure *" : "Raison sociale *"}</label>
              <input className="input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder={isPublic ? "Mairie de Paris, Département…" : "SCI Martin, SARL Dupont…"} data-testid="cli-company" />
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Prénom {isParticulier ? "" : "(contact)"}</label>
              <input className="input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="cli-first" />
            </div>
            <div>
              <label className="label">Nom {isParticulier ? "*" : "(contact)"}</label>
              <input className="input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="cli-last" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Email</label>
              <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cli-email" />
            </div>
            <div>
              <label className="label">Téléphone</label>
              <input className="input font-mono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cli-phone" />
            </div>
          </div>
          <div>
            <label className="label">Adresse postale</label>
            <input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="N° et rue" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Code postal</label>
              <input className="input font-mono" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="label">Ville</label>
              <input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>

          {/* --- Bloc conformité électronique (Pro/Public uniquement) --- */}
          {(isPro || isPublic) && (
            <div className="rounded-lg p-4 space-y-3" style={{ background: "rgba(255,196,0,0.04)", border: "1px solid rgba(255,196,0,0.25)" }} data-testid="einvoice-block">
              <div className="text-xs font-semibold flex items-center gap-2" style={{ color: "var(--pit-yellow)" }}>
                📋 CONFORMITÉ FACTURATION ÉLECTRONIQUE 2027
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">SIRET * <span className="text-[10px] font-normal" style={{ color: "var(--grey)" }}>(14 chiffres)</span></label>
                  <input className="input font-mono" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="12345678900012" maxLength={17} data-testid="cli-siret" />
                </div>
                <div>
                  <label className="label">N° TVA intracom. <span className="text-[10px] font-normal" style={{ color: "var(--grey)" }}>(UE, optionnel)</span></label>
                  <input className="input font-mono" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="FR12345678901" data-testid="cli-vat" />
                </div>
              </div>
              {isPro && (
                <div>
                  <label className="label">Identifiant de routage PDP <span className="text-[10px] font-normal" style={{ color: "var(--grey)" }}>(optionnel — fourni par le client)</span></label>
                  <input className="input font-mono" value={form.routing_id} onChange={(e) => setForm({ ...form, routing_id: e.target.value })} placeholder="Ex. FR-SIRET-CODE ou identifiant Peppol" data-testid="cli-routing" />
                  <div className="text-[11px] mt-1" style={{ color: "var(--grey)" }}>
                    Demandez à votre client s&apos;il a un identifiant de routage. Sinon, la facture sera routée automatiquement via son SIRET.
                  </div>
                </div>
              )}
              {isPublic && (
                <div>
                  <label className="label">Code service Chorus Pro * <span className="text-[10px] font-normal" style={{ color: "var(--grey)" }}>(fourni par l&apos;administration)</span></label>
                  <input className="input font-mono" value={form.chorus_service_code} onChange={(e) => setForm({ ...form, chorus_service_code: e.target.value })} placeholder="Ex. SERVICE_FIN_MAIRIE" data-testid="cli-chorus" />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label">Statut</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea rows={3} className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-2 sticky bottom-0 bg-[var(--surface)]">
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="cli-save">
            <Save size={14} /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
